import { useEffect, useMemo, useRef, useState } from 'react';
import SalesLayout from '../../components/SalesLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const EMPTY_FORM_STATE = {
    course_short_Name: '',
    Course_full_Name: '',
    course_Discription: '',
    course_payment: '0',
    course_University_payment: '0',
    course_certificate_Payment: '0',
    course_part_payment_discount: '0',
    course_full_payment_discount: '0',
    course_payment_Discription: '',
};

const TAX_PERCENT = 2.5;
const TAX_RATE = TAX_PERCENT / 100;

const toFiniteNumberOrNull = (value) => {
    if (value === null || typeof value === 'undefined') {
        return null;
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }

    return numeric;
};

const roundToTwo = (value) => {
    if (!Number.isFinite(value)) {
        return 0;
    }

    return Math.round((value + Number.EPSILON) * 100) / 100;
};

const buildDiscountBreakdown = (
    baseValue,
    discountValue,
) => {
    const baseInput = toFiniteNumberOrNull(baseValue);
    if (baseInput === null) {
        return null;
    }

    const base = roundToTwo(Math.max(baseInput, 0));
    const discountInput = toFiniteNumberOrNull(discountValue) ?? 0;
    const normalizedDiscount = roundToTwo(Math.max(discountInput, 0));
    const discount = Math.min(normalizedDiscount, base);
    const afterDiscount = roundToTwo(Math.max(base - discount, 0));
    const tax = roundToTwo(afterDiscount * TAX_RATE);
    const final = roundToTwo(afterDiscount + tax);

    return {
        discount,
        afterDiscount,
        tax,
        final,
    };
};

const formatCurrencyLKR = (value) => {
    if (value === null || typeof value === 'undefined') {
        return 'Not set';
    }
    return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        minimumFractionDigits: 2,
    }).format(value);
};

const formatCurrencyUSD = (value) => {
    if (value === null || typeof value === 'undefined') {
        return 'Not set';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(value);
};

const resolveStaffAvatar = (imagePath) => imagePath || '';

export default function CourseInfo({ pageTitle, section }) {
    const [courses, setCourses] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [editingCourse, setEditingCourse] = useState(null);
    const [formState, setFormState] = useState(EMPTY_FORM_STATE);
    const [validationErrors, setValidationErrors] = useState({});
    const [processing, setProcessing] = useState(false);
    const [copiedCourseId, setCopiedCourseId] = useState(null);
    const [manualCopyModal, setManualCopyModal] = useState(null);
    const courseList = Array.isArray(courses) ? courses : [];
    const totalCourses = courseList.length;
    const copyFeedbackTimeout = useRef(null);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const loadCourses = async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await axiosInstance.get('/admin/sales-course-info');
                if (cancelled) return;
                const data = resp?.data || {};
                setCourses(Array.isArray(data.courses) ? data.courses : []);

                const sender = data.sender || null;
                setCurrentUser({
                    name: sender?.name ?? 'Staff',
                    position: sender?.positionName ?? sender?.title ?? null,
                    phone: sender?.mobileNumber ?? null,
                    email: sender?.officeEmail ?? null,
                });
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load sales course info', err);
                setError('Failed to load course info. Please try again later.');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadCourses();

        return () => {
            cancelled = true;
            if (copyFeedbackTimeout.current !== null) {
                window.clearTimeout(copyFeedbackTimeout.current);
            }
        };
    }, []);

    const messageVariantOptions = [
        { value: 'all', label: 'All' },
        { value: 'full', label: 'Full' },
        { value: 'part', label: 'Part' },
    ];

    const filteredCourses = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return courseList;
        }

        return courseList.filter((course) => {
            const haystack = `${course.fullName ?? ''} ${course.shortName ?? ''}`.toLowerCase();
            return haystack.includes(query);
        });
    }, [courseList, search]);

    const monetaryToString = (value) => {
        if (value === null || typeof value === 'undefined') {
            return '0';
        }

        return Number(value).toString();
    };

    const openEditModal = (course) => {
        const pricing = course.pricing || {
            tuition: 0,
            university: 0,
            certificate: 0,
            partPaymentDiscount: 0,
            fullPaymentDiscount: 0,
            notes: '',
        };

        setEditingCourse(course);
        setIsCreating(false);
        setFormState({
            course_short_Name: course.shortName ?? '',
            Course_full_Name: course.fullName ?? '',
            course_Discription: course.description ?? '',
            course_payment: monetaryToString(pricing.tuition),
            course_University_payment: monetaryToString(pricing.university),
            course_certificate_Payment: monetaryToString(pricing.certificate),
            course_part_payment_discount: monetaryToString(pricing.partPaymentDiscount),
            course_full_payment_discount: monetaryToString(pricing.fullPaymentDiscount),
            course_payment_Discription: pricing.notes ?? '',
        });
        setValidationErrors({});
        setProcessing(false);
    };

    const closeModal = () => {
        setEditingCourse(null);
        setIsCreating(false);
        setFormState(EMPTY_FORM_STATE);
        setValidationErrors({});
        setProcessing(false);
    };

    const openCreateModal = () => {
        setEditingCourse(null);
        setIsCreating(true);
        setFormState(EMPTY_FORM_STATE);
        setValidationErrors({});
        setProcessing(false);
    };

    const handleFormFieldChange = (event) => {
        const { name, value } = event.target;
        const fieldName = name;

        setFormState((previous) => ({
            ...previous,
            [fieldName]: value,
        }));

        setValidationErrors((previous) => {
            if (!previous[fieldName]) {
                return previous;
            }

            const next = { ...previous };
            delete next[fieldName];
            return next;
        });
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();

        setProcessing(true);
        setValidationErrors({});

        const trimOrNull = (value) => {
            const normalized = value.trim();
            return normalized.length > 0 ? normalized : null;
        };

        const numericRequired = (value) => {
            const normalized = value.trim();
            return normalized.length > 0 ? normalized : '0';
        };

        const numericOptional = (value) => {
            const normalized = value.trim();
            return normalized.length > 0 ? normalized : '0';
        };

        const payload = {
            course_short_Name: formState.course_short_Name.trim(),
            Course_full_Name: formState.Course_full_Name.trim(),
            course_Discription: trimOrNull(formState.course_Discription),
            course_payment: numericRequired(formState.course_payment),
            course_University_payment: numericRequired(formState.course_University_payment),
            course_certificate_Payment: numericRequired(formState.course_certificate_Payment),
            course_part_payment_discount: numericOptional(formState.course_part_payment_discount),
            course_full_payment_discount: numericOptional(formState.course_full_payment_discount),
            course_payment_Discription: trimOrNull(formState.course_payment_Discription),
        };
        try {
            const isCreate = !editingCourse;
            const resp = isCreate
                ? await axiosInstance.post('/admin/sales-course-info', payload)
                : await axiosInstance.put(`/admin/sales-course-info/${editingCourse.id}`, payload);
            const updated = resp?.data?.course;
            if (updated && updated.id) {
                setCourses((previous) => {
                    if (isCreate) {
                        return [...previous, { ...updated }];
                    }
                    return previous.map((course) => (course.id === updated.id ? { ...course, ...updated } : course));
                });
            }
            closeModal();
        } catch (err) {
            const resp = err?.response;
            const respErrors = resp?.data?.errors;
            if (resp && resp.status === 422 && respErrors && typeof respErrors === 'object') {
                setValidationErrors(respErrors);
            } else {
                // eslint-disable-next-line no-console
                console.error('Failed to update course info', err);
            }
        } finally {
            setProcessing(false);
        }
    };

    const formatCurrencyLKRForMessage = (value) => {
        if (value === null || typeof value === 'undefined') {
            return null;
        }
        return new Intl.NumberFormat('en-LK', {
            style: 'currency',
            currency: 'LKR',
            minimumFractionDigits: 2,
        }).format(value);
    };

    const formatCurrencyUSDForMessage = (value) => {
        if (value === null || typeof value === 'undefined') {
            return null;
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(value);
    };

    const formatCurrency = (value) => formatCurrencyLKR(value);
    const formatCurrencyForMessage = (value) => formatCurrencyLKRForMessage(value);

    const buildWhatsAppMessage = (course, variant = 'all') => {
        const pricing = course.pricing || {
            tuition: null,
            university: null,
            certificate: null,
            partPaymentDiscount: null,
            fullPaymentDiscount: null,
            notes: null,
        };

        const lines = [];
        const courseName = course.fullName || course.shortName || 'our program';
        lines.push(`Hello! Thank you for your interest in the ${courseName} at Kaaryaalaya.`);

        if (course.shortName) {
            lines.push(`Course code: ${course.shortName}`);
        }

        const tuition = formatCurrencyLKRForMessage(pricing.tuition);
        const university = formatCurrencyUSDForMessage(pricing.university);
        const certificate = formatCurrencyUSDForMessage(pricing.certificate);
        const partBreakdown = buildDiscountBreakdown(pricing.tuition, pricing.partPaymentDiscount);
        const fullBreakdown = buildDiscountBreakdown(pricing.tuition, pricing.fullPaymentDiscount);
        const includePart = variant === 'all' || variant === 'part';
        const includeFull = variant === 'all' || variant === 'full';

        lines.push('');
        lines.push('Course payment details:');
        lines.push(`Course Payment: ${tuition ?? 'Not set'}`);
        lines.push(`University Payment: ${university ?? 'Not set'}`);
        lines.push(`Certificate Payment: ${certificate ?? 'Not set'}`);

        if (includePart && partBreakdown) {
            lines.push('');
            lines.push('Part payment option:');
            lines.push(`Part Payment Discount: ${formatCurrencyForMessage(partBreakdown.discount) ?? 'Not set'}`);
            lines.push(`After Part Payment Discount: ${formatCurrencyForMessage(partBreakdown.afterDiscount) ?? 'Not set'}`);
            lines.push(`Tax Amount (2.5%): ${formatCurrencyForMessage(partBreakdown.tax) ?? 'Not set'}`);
            lines.push(`Final Amount (Part Payment): ${formatCurrencyForMessage(partBreakdown.final) ?? 'Not set'}`);
        }

        if (includeFull && fullBreakdown) {
            lines.push('');
            lines.push('Full payment option:');
            lines.push(`Full Payment Discount: ${formatCurrencyForMessage(fullBreakdown.discount) ?? 'Not set'}`);
            lines.push(`After Full Payment Discount: ${formatCurrencyForMessage(fullBreakdown.afterDiscount) ?? 'Not set'}`);
            lines.push(`Tax Amount (2.5%): ${formatCurrencyForMessage(fullBreakdown.tax) ?? 'Not set'}`);
            lines.push(`Final Amount (Full Payment): ${formatCurrencyForMessage(fullBreakdown.final) ?? 'Not set'}`);
        }

        if (pricing.notes) {
            lines.push('');
            lines.push('Quick note:');
            lines.push(pricing.notes);
        }

        const totalInquiries = course.stats?.totalInquiries ?? 0;
        if (totalInquiries > 0) {
            lines.push('');
            lines.push(`This course is in demand — ${totalInquiries} recent inquiries have come through.`);
        }

        lines.push('');
        lines.push('I can guide you through registration, paperwork, and answer any questions. Shall I reserve a seat for you or schedule a quick call?');
        lines.push('');
        const signatureLines = [];
        const user = currentUser || {
            name: 'Staff',
            position: null,
            phone: null,
            email: null,
        };
        signatureLines.push(user.name);
        if (user.position) signatureLines.push(String(user.position));
        if (user.phone) signatureLines.push(`Contact: ${user.phone}`);
        if (user.email) signatureLines.push(`Office Email: ${user.email}`);
        lines.push(...signatureLines);

        return lines.join('\n');
    };

    const handleCopyMessage = (course) => {
        setCopiedCourseId(null);
        setManualCopyModal({ course, variant: 'all' });
    };

    const manualCopyMessageText = manualCopyModal ? buildWhatsAppMessage(manualCopyModal.course, manualCopyModal.variant) : '';

    if (loading) {
        return (
            <SalesLayout title={pageTitle} currentSection={section}>
                <PageLoader label="Loading course information…" />
            </SalesLayout>
        );
    }

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            <div className="space-y-6">
                {error && (
                    <section className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                        {error}
                    </section>
                )}
                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 className="text-base font-semibold text-slate-900">Course catalog</h2>
                            <p className="mt-1 text-sm text-slate-500">Search by full name or short code to prep for your next conversation.</p>
                        </div>
                        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
                            <div className="relative w-full md:w-80">
                                <input
                                    type="search"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search courses..."
                                    className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                />
                                <svg
                                    className="absolute left-3 top-2.5 h-5 w-5 text-slate-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-4.35-4.35M10.5 6a4.5 4.5 0 100 9 4.5 4.5 0 000-9z"
                                    />
                                </svg>
                            </div>
                            <button
                                type="button"
                                onClick={openCreateModal}
                                className="inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1"
                            >
                                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add course info
                            </button>
                        </div>
                    </div>
                    <p className="mt-4 text-xs text-slate-500">
                        Showing <span className="font-semibold text-slate-700">{filteredCourses.length}</span> of{' '}
                        <span className="font-semibold text-slate-700">{totalCourses}</span> courses
                    </p>
                </section>

                {filteredCourses.length > 0 ? (
                    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {filteredCourses.map((course) => {
                            const pricing = course.pricing || {
                                tuition: null,
                                university: null,
                                certificate: null,
                                partPaymentDiscount: null,
                                fullPaymentDiscount: null,
                                notes: null,
                            };

                            const amounts = [pricing.tuition, pricing.university, pricing.certificate];
                            const hasAmounts = amounts.some((amount) => amount !== null && typeof amount !== 'undefined');
                            const estimatedTotal = hasAmounts
                                ? (amounts[0] ?? 0) + (amounts[1] ?? 0) + (amounts[2] ?? 0)
                                : null;
                            const partBreakdown = buildDiscountBreakdown(pricing.tuition, pricing.partPaymentDiscount);
                            const fullBreakdown = buildDiscountBreakdown(pricing.tuition, pricing.fullPaymentDiscount);

                            const creator = course.createdBy;
                            const creatorName = creator?.name ?? 'Unknown staff';
                            const creatorAvatar = resolveStaffAvatar(creator?.imagePath);
                            const stats = course.stats ?? { totalInquiries: 0, assignedInquiries: 0 };
                            const totalCount = Number.isFinite(stats.totalInquiries) ? stats.totalInquiries : 0;
                            const totalLabel = totalCount === 1 ? 'Total inquiry' : 'Total inquiries';

                            return (
                                <article
                                    key={course.id}
                                    className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
                                >
                                    <div className="flex flex-1 flex-col gap-4">
                                        <div>
                                            {course.shortName && (
                                                <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">{course.shortName}</p>
                                            )}
                                            <h3 className="mt-1 text-lg font-semibold text-slate-900">
                                                {course.fullName || course.shortName || 'Untitled course'}
                                            </h3>
                                            {course.description && (
                                                <p className="mt-2 text-sm text-slate-600">{course.description}</p>
                                            )}
                                            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                                                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                                                    <svg
                                                        className="h-4 w-4 text-slate-500"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 14l9-5-9-5-9 5 9 5zm0 7l9-5-9-5-9 5 9 5z"
                                                        />
                                                    </svg>
                                                    <span>{totalCount}</span>
                                                    <span>{totalLabel}</span>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="grid gap-3 sm:grid-cols-2">
                                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Tuition Fee</p>
                                                    <p className="mt-1 text-sm font-semibold text-slate-800">{formatCurrencyLKR(pricing.tuition)}</p>
                                                </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">University Fee</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{formatCurrencyUSD(pricing.university)}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Certificate Fee</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{formatCurrencyUSD(pricing.certificate)}</p>
                                            </div>
                                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Estimated Total</p>
                                                <p className="mt-1 text-sm font-semibold text-slate-800">{formatCurrency(estimatedTotal)}</p>
                                            </div>
                                        </div>

                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {pricing.partPaymentDiscount && pricing.partPaymentDiscount !== 0 ? (
                                                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                                                    Part-payment discount {formatCurrency(pricing.partPaymentDiscount)}
                                                </span>
                                            ) : null}
                                            {pricing.fullPaymentDiscount && pricing.fullPaymentDiscount !== 0 ? (
                                                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                                                    Full-payment discount {formatCurrency(pricing.fullPaymentDiscount)}
                                                </span>
                                            ) : null}
                                            {!pricing.partPaymentDiscount && !pricing.fullPaymentDiscount && (
                                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                                                    No active discounts
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment breakdown</p>
                                            <div className="mt-3 space-y-2">
                                                <div className="flex items-center justify-between gap-3">
                                                    <span>Course Payment:</span>
                                                    <span className="font-semibold text-slate-900">{formatCurrencyLKR(pricing.tuition)}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-3">
                                                    <span>University Payment:</span>
                                                    <span className="font-semibold text-slate-900">{formatCurrencyUSD(pricing.university)}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-3">
                                                    <span>Certificate Payment:</span>
                                                    <span className="font-semibold text-slate-900">{formatCurrencyUSD(pricing.certificate)}</span>
                                                </div>
                                            </div>

                                            {partBreakdown && (
                                                <div className="mt-4 space-y-2 border-t border-slate-200 pt-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Part payment</p>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Part Payment Discount:</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(partBreakdown.discount)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>After Part Payment Discount:</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(partBreakdown.afterDiscount)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Tax Amount (2.5%):</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(partBreakdown.tax)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Final Amount (Part Payment):</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(partBreakdown.final)}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {fullBreakdown && (
                                                <div className="mt-4 space-y-2 border-t border-slate-200 pt-3">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Full payment</p>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Full Payment Discount:</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(fullBreakdown.discount)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>After Full Payment Discount:</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(fullBreakdown.afterDiscount)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Tax Amount (2.5%):</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(fullBreakdown.tax)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span>Final Amount (Full Payment):</span>
                                                        <span className="font-semibold text-slate-900">{formatCurrency(fullBreakdown.final)}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {pricing.notes && (
                                            <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                                                {pricing.notes}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 border-t border-slate-100 pt-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={getImageURL(creatorAvatar)}
                                                    alt={creatorName}
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                                <div>
                                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Created by</p>
                                                    <p className="text-sm font-semibold text-slate-700">{creatorName}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyMessage(course)}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-1"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 16h8a2 2 0 002-2V6a2 2 0 00-2-2h-8a2 2 0 00-2 2v8zm0 0v2a2 2 0 002 2h6"
                                                        />
                                                    </svg>
                                                    {copiedCourseId === course.id ? 'Copied!' : 'Copy WhatsApp message'}
                                                </button>


                                                <button
                                                    type="button"
                                                    onClick={() => openEditModal(course)}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-600 transition hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-200 focus:ring-offset-1"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M16.862 3.487l3.651 3.651m-2.475-6.126a2.5 2.5 0 013.536 3.536L7.5 18.622 3 19.5l.878-4.5L18.038 1.012z"
                                                        />
                                                    </svg>
                                                    Update
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </section>
                ) : (
                    <section className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center">
                        <h3 className="text-base font-semibold text-slate-700">
                            {search.trim() ? 'No courses match your search yet.' : 'No course information available yet.'}
                        </h3>
                        <p className="mt-2 text-sm text-slate-500">
                            {search.trim()
                                ? 'Try a different course name or short code.'
                                : 'Once courses are created, you will see their quick facts here.'}
                        </p>
                    </section>
                )}
            </div>

            {(editingCourse || isCreating) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6">
                    <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">
                                    {isCreating
                                        ? 'Add new course info'
                                        : `Update ${editingCourse.shortName || editingCourse.fullName || 'Course'}`}
                                </h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    {isCreating
                                        ? 'Create a new course entry with pricing, descriptions, and talking points.'
                                        : 'Adjust pricing, descriptions, and talking points before your next conversation.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                aria-label="Close update form"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form className="mt-6 space-y-5" onSubmit={handleFormSubmit}>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700" htmlFor="Course_full_Name">
                                        Full course name
                                    </label>
                                    <input
                                        id="Course_full_Name"
                                        name="Course_full_Name"
                                        type="text"
                                        value={formState.Course_full_Name}
                                        onChange={handleFormFieldChange}
                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                        required
                                    />
                                    {validationErrors.Course_full_Name && (
                                        <p className="mt-1 text-xs text-rose-600">{validationErrors.Course_full_Name}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700" htmlFor="course_short_Name">
                                        Short name / code
                                    </label>
                                    <input
                                        id="course_short_Name"
                                        name="course_short_Name"
                                        type="text"
                                        value={formState.course_short_Name}
                                        onChange={handleFormFieldChange}
                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                        required
                                    />
                                    {validationErrors.course_short_Name && (
                                        <p className="mt-1 text-xs text-rose-600">{validationErrors.course_short_Name}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700" htmlFor="course_Discription">
                                    Course description
                                </label>
                                <textarea
                                    id="course_Discription"
                                    name="course_Discription"
                                    value={formState.course_Discription}
                                    onChange={handleFormFieldChange}
                                    rows={3}
                                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                />
                                {validationErrors.course_Discription && (
                                    <p className="mt-1 text-xs text-rose-600">{validationErrors.course_Discription}</p>
                                )}
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-slate-700">Pricing</h4>
                                <div className="mt-3 grid gap-4 md:grid-cols-3">
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="course_payment">
                                            Tuition fee
                                        </label>
                                        <input
                                            id="course_payment"
                                            name="course_payment"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formState.course_payment}
                                            onChange={handleFormFieldChange}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                            required
                                        />
                                        {validationErrors.course_payment && (
                                            <p className="mt-1 text-xs text-rose-600">{validationErrors.course_payment}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="course_University_payment">
                                            University fee
                                        </label>
                                        <input
                                            id="course_University_payment"
                                            name="course_University_payment"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formState.course_University_payment}
                                            onChange={handleFormFieldChange}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                            required
                                        />
                                        {validationErrors.course_University_payment && (
                                            <p className="mt-1 text-xs text-rose-600">{validationErrors.course_University_payment}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="course_certificate_Payment">
                                            Certificate fee
                                        </label>
                                        <input
                                            id="course_certificate_Payment"
                                            name="course_certificate_Payment"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formState.course_certificate_Payment}
                                            onChange={handleFormFieldChange}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                            required
                                        />
                                        {validationErrors.course_certificate_Payment && (
                                            <p className="mt-1 text-xs text-rose-600">{validationErrors.course_certificate_Payment}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-slate-700">Discounts</h4>
                                <div className="mt-3 grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="course_part_payment_discount">
                                            Part-payment discount
                                        </label>
                                        <input
                                            id="course_part_payment_discount"
                                            name="course_part_payment_discount"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formState.course_part_payment_discount}
                                            onChange={handleFormFieldChange}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                        />
                                        {validationErrors.course_part_payment_discount && (
                                            <p className="mt-1 text-xs text-rose-600">{validationErrors.course_part_payment_discount}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500" htmlFor="course_full_payment_discount">
                                            Full-payment discount
                                        </label>
                                        <input
                                            id="course_full_payment_discount"
                                            name="course_full_payment_discount"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formState.course_full_payment_discount}
                                            onChange={handleFormFieldChange}
                                            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                        />
                                        {validationErrors.course_full_payment_discount && (
                                            <p className="mt-1 text-xs text-rose-600">{validationErrors.course_full_payment_discount}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700" htmlFor="course_payment_Discription">
                                    Pricing notes / talking points
                                </label>
                                <textarea
                                    id="course_payment_Discription"
                                    name="course_payment_Discription"
                                    value={formState.course_payment_Discription}
                                    onChange={handleFormFieldChange}
                                    rows={3}
                                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                />
                                {validationErrors.course_payment_Discription && (
                                    <p className="mt-1 text-xs text-rose-600">{validationErrors.course_payment_Discription}</p>
                                )}
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={processing}
                                    className="inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-sky-400"
                                >
                                    {processing ? 'Saving...' : 'Save changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {manualCopyModal !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6">
                    <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900">Copy message manually</h3>
                                <p className="mt-1 text-sm text-slate-500">Clipboard access was blocked. Choose the details you want included, then copy.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setManualCopyModal(null)}
                                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                aria-label="Close manual copy"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mt-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message preset</p>
                            <div className="mt-2 inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1">
                                {messageVariantOptions.map(({ value, label }) => {
                                    const isActive = manualCopyModal.variant === value;
                                    const buttonClasses = [
                                        'rounded-md px-3 py-1 text-xs font-semibold transition',
                                        isActive ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-800',
                                    ].join(' ');

                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            className={buttonClasses}
                                            onClick={() =>
                                                setManualCopyModal((previous) =>
                                                    previous ? { ...previous, variant: value } : previous,
                                                )
                                            }
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-4">
                            <textarea
                                readOnly
                                value={manualCopyMessageText}
                                rows={10}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>

                        <div className="mt-4 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setManualCopyModal(null)}
                                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const manualState = manualCopyModal;
                                    if (!manualState) {
                                        return;
                                    }
                                    const text = manualCopyMessageText;
                                    try {
                                        const textarea = document.createElement('textarea');
                                        textarea.value = text;
                                        textarea.setAttribute('readonly', 'true');
                                        textarea.style.position = 'fixed';
                                        textarea.style.top = '-9999px';
                                        textarea.style.opacity = '0';
                                        document.body.appendChild(textarea);
                                        textarea.focus();
                                        textarea.select();
                                        textarea.setSelectionRange(0, textarea.value.length);
                                        const ok = document.execCommand('copy');
                                        document.body.removeChild(textarea);
                                        if (ok) {
                                            setManualCopyModal(null);
                                            setCopiedCourseId(manualState.course.id);
                                            if (copyFeedbackTimeout.current !== null) {
                                                window.clearTimeout(copyFeedbackTimeout.current);
                                            }
                                            copyFeedbackTimeout.current = window.setTimeout(() => {
                                                setCopiedCourseId((current) =>
                                                    current === manualState.course.id ? null : current,
                                                );
                                            }, 2000);
                                        } else {
                                            // As a last resort, prompt
                                            window.prompt('Copy the following message:', text);
                                            setManualCopyModal(null);
                                        }
                                    } catch (err) {
                                        window.prompt('Copy the following message:', text);
                                        setManualCopyModal(null);
                                    }
                                }}
                                className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SalesLayout>
    );
}
